import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import { io } from 'socket.io-client';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import ChatNotification from '../components/ChatNotification';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessageCircle, Users, Send, Paperclip, Search, Plus, 
  User, ChevronLeft, X, Check, CheckCheck, File, Image,
  Bell, UserPlus, Settings, Smile, ArrowDown, Play, Reply, SmilePlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Giphy API Key (SDK Development Key - for development use)
const GIPHY_API_KEY = 'GlVGYHkr3WSBnllca54iNt0yFbjz7L65';

// Check if message is emoji-only (supports all Unicode emoji)
const isEmojiOnly = (text) => {
  if (!text) return false;
  // Remove all emoji and whitespace, check if anything remains
  const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{231A}-\u{231B}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{25AA}-\u{25AB}]|[\u{25B6}]|[\u{25C0}]|[\u{25FB}-\u{25FE}]|[\u{2614}-\u{2615}]|[\u{2648}-\u{2653}]|[\u{267F}]|[\u{2693}]|[\u{26A1}]|[\u{26AA}-\u{26AB}]|[\u{26BD}-\u{26BE}]|[\u{26C4}-\u{26C5}]|[\u{26CE}]|[\u{26D4}]|[\u{26EA}]|[\u{26F2}-\u{26F3}]|[\u{26F5}]|[\u{26FA}]|[\u{26FD}]|[\u{2702}]|[\u{2705}]|[\u{2708}-\u{270D}]|[\u{270F}]|[\u{2712}]|[\u{2714}]|[\u{2716}]|[\u{271D}]|[\u{2721}]|[\u{2728}]|[\u{2733}-\u{2734}]|[\u{2744}]|[\u{2747}]|[\u{274C}]|[\u{274E}]|[\u{2753}-\u{2755}]|[\u{2757}]|[\u{2763}-\u{2764}]|[\u{2795}-\u{2797}]|[\u{27A1}]|[\u{27B0}]|[\u{27BF}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{2B50}]|[\u{2B55}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]|[\u{FE0F}]|[\u{200D}]/gu;
  const withoutEmoji = text.replace(emojiRegex, '').replace(/\s/g, '');
  return withoutEmoji.length === 0 && text.trim().length > 0;
};

// Count emojis in text
const countEmojis = (text) => {
  if (!text) return 0;
  const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]/gu;
  const matches = text.match(emojiRegex);
  return matches ? matches.length : 0;
};

// Soft chime generator using Web Audio API
const playSoftChime = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a gentle oscillator for the chime
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Use sine wave for soft, pleasant tone
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(830, audioContext.currentTime);
    
    // Gentle volume envelope
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.4);
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.4);
    
    setTimeout(() => audioContext.close(), 500);
  } catch (e) {
    console.log('Web Audio not supported');
  }
};

const Chat = ({ user, onLogout }) => {
  const [conversations, setConversations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [inAppNotification, setInAppNotification] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const [showNotifBanner, setShowNotifBanner] = useState(Notification.permission === 'default');
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearchTerm, setGifSearchTerm] = useState('');
  const [gifs, setGifs] = useState([]);
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [participantNames, setParticipantNames] = useState({});
  const [participants, setParticipants] = useState([]);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);
  const workerRef = useRef(null);
  const lastSoundPlayedRef = useRef(0);
  const messageInputRef = useRef(null);
  const shouldScrollRef = useRef(true);
  const socketRef = useRef(null);
  const gifSearchTimeout = useRef(null);
  
  // Play soft notification chime (with debounce)
  const playNotificationSound = useCallback(() => {
    try {
      const now = Date.now();
      if (now - lastSoundPlayedRef.current < 1000) return;
      lastSoundPlayedRef.current = now;
      playSoftChime();
    } catch (e) {
      console.log('Audio error:', e);
    }
  }, []);

  // Show in-app notification
  const showInAppNotification = useCallback((notificationData) => {
    // Don't show notification for messages in the currently open chat
    if (selectedChat && selectedChat.id === notificationData.conversationId) {
      return;
    }
    
    // Play sound
    playNotificationSound();
    
    // Always show browser notification when tab is not focused or hidden
    if (document.hidden || !document.hasFocus()) {
      if (Notification.permission === 'granted') {
        const title = notificationData.type === 'group' 
          ? `${notificationData.senderName} in ${notificationData.groupName}`
          : notificationData.senderName || 'New Message';
        
        const notification = new Notification(title, {
          body: notificationData.message,
          icon: '/logo192.png',
          tag: notificationData.conversationId || 'chat-notification',
          requireInteraction: false,
          silent: false
        });
        
        // Handle notification click - focus the window
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }
    }
    
    // Show in-app notification toast
    setInAppNotification({
      id: notificationData.messageId || Date.now(),
      type: notificationData.type,
      senderName: notificationData.senderName,
      groupName: notificationData.groupName,
      message: notificationData.message,
      conversationId: notificationData.conversationId
    });
  }, [selectedChat, playNotificationSound]);

  // Initialize Socket.IO connection (optional - polling fallback handles real-time)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Try to connect via Socket.IO for instant notifications
    try {
      socketRef.current = io(BACKEND_URL.replace('/api', ''), {
        transports: ['polling'],
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 2000,
        timeout: 5000,
        path: '/socket.io/'
      });

      socketRef.current.on('connect', () => {
        console.log('Socket connected');
        socketRef.current.emit('register_user', { token });
      });

      socketRef.current.on('new_message', (data) => {
        console.log('Real-time message:', data);
        showInAppNotification(data);
        if (selectedChat && selectedChat.id === data.conversationId) {
          fetchMessages();
        }
        fetchConversations();
      });

      socketRef.current.on('connect_error', () => {
        // Silently fall back to polling
        console.log('Socket unavailable, using polling');
      });
    } catch (e) {
      console.log('Socket initialization failed, using polling');
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [showInAppNotification]);

  // Handle notification click - navigate to chat
  const handleNotificationClick = useCallback((notif) => {
    if (notif.conversationId) {
      const chat = [...conversations, ...groups].find(c => c.id === notif.conversationId);
      if (chat) {
        setSelectedChat(chat);
      }
    }
    setInAppNotification(null);
  }, [conversations, groups]);

  // Request notification permission
  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      setShowNotifBanner(false);
      if (permission === 'granted') {
        // Show a test notification
        new Notification('Notifications Enabled!', {
          body: 'You will now receive chat notifications even when on other tabs.',
          icon: '/logo192.png'
        });
      }
    } catch (e) {
      console.error('Failed to request notification permission:', e);
    }
  };

  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const [convRes, groupRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/chat/conversations`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }),
        axios.get(`${BACKEND_URL}/api/chat/groups`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        })
      ]);
      setConversations(convRes.data.conversations || []);
      setGroups(groupRes.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error.message);
      // Don't show toast for polling errors to avoid spam
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${BACKEND_URL}/api/chat/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      
      const newUnreadCount = response.data.unread_messages || 0;
      setUnreadCount(newUnreadCount);
      
      // Fetch notifications
      const notifRes = await axios.get(`${BACKEND_URL}/api/chat/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      const newNotifications = notifRes.data.notifications || [];
      
      // Show in-app notification for new messages
      if (newNotifications.length > 0) {
        const latestNotif = newNotifications[0];
        
        // Show attractive in-app notification
        showInAppNotification({
          type: latestNotif.conversation_id?.includes('group') ? 'group' : 'one-on-one',
          conversationId: latestNotif.conversation_id,
          senderName: latestNotif.title?.replace('New message from ', '') || 'New Message',
          groupName: '',
          message: latestNotif.message,
          messageId: latestNotif.id
        });
        
        // Mark as read
        await axios.post(`${BACKEND_URL}/api/chat/notifications/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        });
      }
      setNotifications(newNotifications);
    } catch (error) {
      console.error('Failed to fetch unread count:', error.message);
    }
  }, [showInAppNotification]);

  const fetchMessages = useCallback(async () => {
    if (!selectedChat) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(
        `${BACKEND_URL}/api/chat/messages/${selectedChat.id}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );
      
      const newMessages = response.data.messages || [];
      setLastMessageCount(newMessages.length);
      setMessages(newMessages);
      if (response.data.participant_names) setParticipantNames(response.data.participant_names);
      if (response.data.participants) setParticipants(response.data.participants);
    } catch (error) {
      console.error('Failed to fetch messages:', error.message);
    }
  }, [selectedChat]);

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login again');
        return;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/chat/users`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again');
      } else {
        toast.error('Failed to load users. Please try again.');
      }
    }
  };

  useEffect(() => {
    // Request notification permission on mount
    if (Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission);
      });
    }
    
    const initializeChat = async () => {
      try {
        // Run all initial fetches in parallel for faster loading
        await Promise.all([
          fetchConversations(),
          fetchAllUsers(),
          fetchUnreadCount()
        ]);
      } catch (error) {
        console.error('Chat initialization error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    initializeChat();
    
    // Use Web Worker for polling - immune to browser background tab throttling
    try {
      const worker = new Worker('/poll-worker.js');
      workerRef.current = worker;
      worker.onmessage = () => {
        fetchConversations();
        if (selectedChat) {
          fetchMessages();
        }
      };
      worker.postMessage({ type: 'start', interval: 5000 });
    } catch (e) {
      // Fallback to setInterval if Worker not supported
      pollingRef.current = setInterval(() => {
        fetchConversations();
        if (selectedChat) {
          fetchMessages();
        }
      }, 5000);
    }
    
    // Handle visibility change - fetch immediately when user returns
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchConversations();
        if (selectedChat) {
          fetchMessages();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'stop' });
        workerRef.current.terminate();
        workerRef.current = null;
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchConversations, fetchUnreadCount, fetchMessages, selectedChat]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages();
    }
  }, [selectedChat, fetchMessages]);

  // Only scroll to bottom when user is already near bottom or just sent a message
  useEffect(() => {
    if (shouldScrollRef.current && isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isNearBottom]);

  // Handle scroll events to track if user is near bottom
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const threshold = 150; // pixels from bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const nearBottom = distanceFromBottom < threshold;
    setIsNearBottom(nearBottom);
    setShowScrollButton(!nearBottom && messages.length > 0);
  }, [messages.length]);

  // Scroll to bottom function
  const scrollToBottom = () => {
    shouldScrollRef.current = true;
    setIsNearBottom(true);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e, messageType = 'text', gifUrl = null) => {
    if (e) e.preventDefault();
    const messageContent = gifUrl || newMessage.trim();
    if (!messageContent || !selectedChat) return;

    setSendingMessage(true);
    shouldScrollRef.current = true;
    setIsNearBottom(true);
    
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Session expired. Please login again');
          setSendingMessage(false);
          return;
        }
        
        await axios.post(
          `${BACKEND_URL}/api/chat/messages`,
          {
            content: messageContent,
            conversation_id: selectedChat.id,
            type: gifUrl ? 'gif' : messageType,
            reply_to: replyTo?.id || null
          },
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000
          }
        );
        
        setNewMessage('');
        setReplyTo(null);
        setShowGifPicker(false);
        fetchMessages();
        fetchConversations();
        
        setTimeout(() => {
          if (messageInputRef.current) {
            messageInputRef.current.style.height = 'auto';
            messageInputRef.current.focus();
          }
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        
        setSendingMessage(false);
        return; // Success, exit function
        
      } catch (error) {
        lastError = error;
        console.error(`Send attempt ${attempt + 1} failed:`, error);
        
        if (error.response?.status === 401) {
          toast.error('Session expired. Please login again');
          setSendingMessage(false);
          return;
        }
        
        if (attempt < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 500)); // Wait before retry
          continue;
        }
      }
    }
    
    // All retries failed
    setSendingMessage(false);
    if (lastError?.response?.status === 404) {
      toast.error('Conversation not found. Please refresh the page.');
    } else if (lastError?.code === 'ECONNABORTED') {
      toast.error('Request timed out. Please try again.');
    } else {
      toast.error('Failed to send message. Please try again.');
    }
  };

  // Search GIFs from Giphy
  const searchGifs = async (query) => {
    setLoadingGifs(true);
    try {
      let url;
      if (!query.trim()) {
        // Load trending GIFs
        url = `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=20&rating=pg`;
      } else {
        // Search GIFs
        url = `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(query)}&limit=20&rating=pg`;
      }
      
      const response = await axios.get(url);
      const gifs = response.data.data?.map(gif => ({
        id: gif.id,
        url: gif.images?.fixed_height?.url || gif.images?.original?.url,
        preview: gif.images?.fixed_width_small?.url || gif.images?.fixed_height?.url,
        title: gif.title
      })) || [];
      setGifs(gifs);
    } catch (error) {
      console.error('Failed to load GIFs:', error);
      setGifs([]);
    }
    setLoadingGifs(false);
  };

  // Handle GIF search with debounce
  const handleGifSearch = (value) => {
    setGifSearchTerm(value);
    if (gifSearchTimeout.current) {
      clearTimeout(gifSearchTimeout.current);
    }
    gifSearchTimeout.current = setTimeout(() => {
      searchGifs(value);
    }, 300);
  };

  // Send GIF message
  const sendGif = (gif) => {
    if (gif.url) {
      handleSendMessage(null, 'gif', gif.url);
    }
  };

  // Quick reaction emojis
  const quickReactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

  // Handle emoji reaction on a message
  const handleReaction = async (messageId, emoji) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/chat/messages/${messageId}/react`,
        { emoji },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
      );
      // Update message reactions locally
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: res.data.reactions } : m));
      setShowReactionPicker(null);
    } catch (e) { toast.error('Failed to react'); }
  };


  // Toggle GIF picker
  const toggleGifPicker = () => {
    setShowGifPicker(!showGifPicker);
    setShowEmojiPicker(false);
    if (!showGifPicker) {
      searchGifs(''); // Load trending on open
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedChat) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again');
        return;
      }
      
      await axios.post(
        `${BACKEND_URL}/api/chat/upload?conversation_id=${selectedChat.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000 // 30 seconds for file upload
        }
      );
      toast.success('File uploaded');
      fetchMessages();
      fetchConversations();
    } catch (error) {
      console.error('Failed to upload file:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again');
      } else if (error.response?.status === 413) {
        toast.error('File too large. Please choose a smaller file.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload file. Please try again.');
      }
    }
    e.target.value = '';
  };

  const startNewChat = async (targetUser) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again');
        return;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/conversations`,
        { participant_email: targetUser.email },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );
      setSelectedChat(response.data);
      setShowNewChat(false);
      setSearchTerm('');
      fetchConversations();
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error.response?.status === 404) {
        toast.error('User not found');
      } else {
        toast.error('Failed to start conversation. Please try again.');
      }
    }
  };

  const createGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      toast.error('Please enter group name and select members');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again');
        return;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/groups`,
        {
          name: groupName,
          member_emails: selectedMembers.map(m => m.email)
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );
      setSelectedChat(response.data);
      setShowNewGroup(false);
      setGroupName('');
      setSelectedMembers([]);
      fetchConversations();
      toast.success('Group created');
    } catch (error) {
      console.error('Failed to create group:', error);
      toast.error('Failed to create group. Please try again.');
    }
  };

  // Add member to existing group
  const addMemberToGroup = async (memberEmail) => {
    if (!selectedChat || selectedChat.type !== 'group') return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again');
        return;
      }
      
      await axios.post(
        `${BACKEND_URL}/api/chat/groups/${selectedChat.id}/members`,
        { member_emails: [memberEmail] },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );
      
      toast.success('Member added to group');
      setShowAddMember(false);
      setAddMemberSearch('');
      fetchConversations();
      
      // Update selected chat with new member
      setSelectedChat(prev => ({
        ...prev,
        participants: [...prev.participants, memberEmail]
      }));
    } catch (error) {
      console.error('Failed to add member:', error);
      toast.error('Failed to add member. Please try again.');
    }
  };

  // Handle emoji selection
  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const getChatName = (chat) => {
    if (chat.type === 'group') return chat.name;
    const otherEmail = chat.participants.find(p => p !== user.email);
    return chat.participant_names?.[otherEmail] || otherEmail;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000 && date.getDate() === now.getDate()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const filteredUsers = allUsers.filter(u =>
    u.agent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allChats = [...conversations.filter(c => c.type === 'one-on-one'), ...groups].sort((a, b) => {
    const dateA = new Date(a.last_message_at || a.created_at);
    const dateB = new Date(b.last_message_at || b.created_at);
    return dateB - dateA;
  });

  return (
    <div className="flex min-h-screen bg-slate-100">
      <Sidebar user={user} onLogout={onLogout} />
      
      {/* Notification Permission Banner */}
      {showNotifBanner && notificationPermission === 'default' && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9998] bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-4 max-w-md">
          <Bell className="w-5 h-5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-sm">Enable Notifications</p>
            <p className="text-xs text-blue-100">Get notified even when on other tabs</p>
          </div>
          <Button
            onClick={requestNotificationPermission}
            size="sm"
            className="bg-white text-blue-600 hover:bg-blue-50 shrink-0"
          >
            Enable
          </Button>
          <button
            onClick={() => setShowNotifBanner(false)}
            className="text-white/70 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* In-App Notification */}
      {inAppNotification && (
        <ChatNotification
          notification={inAppNotification}
          onClose={() => setInAppNotification(null)}
          onClick={() => handleNotificationClick(inAppNotification)}
        />
      )}
      
      <main className="flex-1 p-4 overflow-hidden" data-testid="chat-page">
        <div className="h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-xl overflow-hidden flex">
          {/* Chat List Sidebar */}
          <div className={`w-80 border-r flex flex-col ${selectedChat ? 'hidden md:flex' : 'flex'}`}>
            {/* Header */}
            <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-emerald-600">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <MessageCircle className="w-6 h-6" />
                  Chats
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </h2>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                    onClick={() => { setShowNewChat(true); fetchAllUsers(); }}
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                    onClick={() => { setShowNewGroup(true); fetchAllUsers(); }}
                  >
                    <Users className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search chats..."
                  className="pl-9 bg-white/90 border-0"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Chat List */}
            <ScrollArea className="flex-1">
              {allChats.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No conversations yet</p>
                  <p className="text-sm mt-1">Start a new chat!</p>
                </div>
              ) : (
                allChats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`p-4 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedChat?.id === chat.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                    onClick={() => setSelectedChat(chat)}
                    data-testid={`chat-list-item-${chat.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                        chat.type === 'group' ? 'bg-gradient-to-br from-purple-500 to-pink-500' : 'bg-gradient-to-br from-blue-500 to-emerald-500'
                      }`}>
                        {chat.type === 'group' ? (
                          <Users className="w-6 h-6" />
                        ) : (
                          getChatName(chat).charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {getChatName(chat)}
                          </h3>
                          <span className="text-xs text-slate-400">
                            {formatTime(chat.last_message_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-slate-500 truncate">
                            {chat.last_message || 'No messages yet'}
                          </p>
                          {chat.unread_count > 0 && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full ml-2">
                              {chat.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col relative ${!selectedChat ? 'hidden md:flex' : 'flex'}`}>
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b bg-white shadow-sm flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="md:hidden"
                    onClick={() => setSelectedChat(null)}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    selectedChat.type === 'group' ? 'bg-gradient-to-br from-purple-500 to-pink-500' : 'bg-gradient-to-br from-blue-500 to-emerald-500'
                  }`}>
                    {selectedChat.type === 'group' ? (
                      <Users className="w-5 h-5" />
                    ) : (
                      getChatName(selectedChat).charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{getChatName(selectedChat)}</h3>
                    <p className="text-xs text-slate-500">
                      {selectedChat.type === 'group' 
                        ? `${selectedChat.participants?.length || 0} members` 
                        : 'Online'}
                    </p>
                  </div>
                  {/* Add Member Button for Groups */}
                  {selectedChat.type === 'group' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowAddMember(true); fetchAllUsers(); }}
                      className="text-purple-600 hover:bg-purple-50"
                      title="Add member to group"
                    >
                      <UserPlus className="w-5 h-5" />
                    </Button>
                  )}
                </div>

                {/* Messages */}
                <div 
                  ref={messagesContainerRef}
                  className="flex-1 p-4 bg-gradient-to-b from-slate-50 to-white overflow-y-auto"
                  onScroll={handleScroll}
                >
                  <div className="space-y-3">
                    {messages.map((msg) => {
                      const isOwn = msg.sender_email === user.email;
                      const readByOthers = (msg.read_by || []).filter(e => e !== msg.sender_email);
                      const otherParticipants = participants.filter(e => e !== msg.sender_email);
                      const allRead = otherParticipants.length > 0 && readByOthers.length >= otherParticipants.length;
                      const someRead = readByOthers.length > 0;
                      const isGroup = selectedChat.type === 'group';
                      const reactions = msg.reactions || [];
                      const isHovered = hoveredMsg === msg.id;
                      
                      return (
                        <div
                          key={msg.id}
                          data-testid={`message-row-${msg.id}`}
                          className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group relative`}
                          onMouseEnter={() => setHoveredMsg(msg.id)}
                          onMouseLeave={() => { if (showReactionPicker !== msg.id) setHoveredMsg(null); }}
                        >
                          <div className={`max-w-[70%] ${isOwn ? 'order-2' : ''} relative`}>
                            {!isOwn && isGroup && (
                              <p className="text-xs text-slate-500 mb-1 ml-2">{msg.sender_name}</p>
                            )}

                            {/* Reply preview */}
                            {msg.reply_to && (
                              <div className="mx-1 mb-1 px-3 py-1.5 rounded-lg bg-slate-100 border-l-blue-500 text-xs" style={{ borderLeftWidth: '3px' }} data-testid={`msg-reply-quote-${msg.id}`}>
                                <p className="font-semibold text-slate-600">
                                  {msg.reply_to.sender_name === user.agent_name ? 'You' : msg.reply_to.sender_name}
                                </p>
                                <p className="truncate text-slate-500">
                                  {msg.reply_to.type === 'gif' ? 'GIF' : msg.reply_to.content}
                                </p>
                              </div>
                            )}

                            <div
                              className={`rounded-2xl px-4 py-2 ${
                                isOwn
                                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-md'
                                  : 'bg-white border shadow-sm rounded-bl-md'
                              }`}
                            >
                              {msg.type === 'file' ? (
                                <a
                                  href={`${BACKEND_URL}${msg.file_url}?token=${localStorage.getItem('token')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center gap-2 ${isOwn ? 'text-white' : 'text-blue-600'}`}
                                  download
                                >
                                  {msg.file_name?.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                    <Image className="w-4 h-4" />
                                  ) : (
                                    <File className="w-4 h-4" />
                                  )}
                                  <span className="underline">{msg.file_name}</span>
                                </a>
                              ) : msg.type === 'gif' ? (
                                <img 
                                  src={msg.content} 
                                  alt="GIF" 
                                  className="max-w-[250px] rounded-lg"
                                  loading="lazy"
                                />
                              ) : isEmojiOnly(msg.content) ? (
                                <p className="text-5xl leading-tight">{msg.content}</p>
                              ) : (
                                <p className={`whitespace-pre-wrap break-words ${isOwn ? 'text-white' : 'text-slate-800'}`} style={{ wordBreak: 'break-word' }}>{msg.content}</p>
                              )}

                              {/* Time + Read status */}
                              <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-blue-100' : 'text-slate-400'}`}>
                                <span className="text-xs">
                                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {isOwn && (
                                  allRead ? (
                                    <CheckCheck data-testid={`tick-allread-${msg.id}`} className="w-4 h-4 text-sky-300" />
                                  ) : someRead ? (
                                    <CheckCheck data-testid={`tick-someread-${msg.id}`} className="w-4 h-4 text-blue-200/60" />
                                  ) : (
                                    <Check data-testid={`tick-sent-${msg.id}`} className="w-4 h-4" />
                                  )
                                )}
                              </div>
                            </div>

                            {/* Read by names (own messages only, on hover) */}
                            {isOwn && isHovered && readByOthers.length > 0 && (
                              <div className="text-right mr-1 mt-0.5">
                                <span className="text-[10px] text-slate-400" data-testid={`seen-by-${msg.id}`}>
                                  Seen by {readByOthers.map(e => participantNames[e] || e.split('@')[0]).join(', ')}
                                </span>
                              </div>
                            )}

                            {/* Reactions display */}
                            {reactions.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1 ml-1" data-testid={`reactions-${msg.id}`}>
                                {reactions.map((r, ri) => (
                                  <button key={ri} onClick={() => handleReaction(msg.id, r.emoji)}
                                    data-testid={`reaction-badge-${msg.id}-${r.emoji}`}
                                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white border shadow-sm text-xs hover:bg-slate-50 transition-colors"
                                    title={r.users.map(u => u.name).join(', ')}>
                                    <span>{r.emoji}</span>
                                    <span className="text-slate-500 text-[10px] font-medium">{r.users.length}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Hover actions: Reply + React */}
                          {isHovered && (
                            <div className={`flex items-center gap-0.5 self-start mt-6 ${isOwn ? 'order-1 mr-1' : 'ml-1'}`}>
                              <button onClick={() => { setReplyTo({ id: msg.id, content: msg.content, sender_name: msg.sender_name, type: msg.type }); messageInputRef.current?.focus(); }}
                                data-testid={`reply-btn-${msg.id}`}
                                className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-blue-500 transition-colors" title="Reply">
                                <Reply className="w-3.5 h-3.5" />
                              </button>
                              <div className="relative">
                                <button onClick={() => setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id)}
                                  data-testid={`react-btn-${msg.id}`}
                                  className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-amber-500 transition-colors" title="React">
                                  <SmilePlus className="w-3.5 h-3.5" />
                                </button>
                                {showReactionPicker === msg.id && (
                                  <div className={`absolute ${isOwn ? 'right-0' : 'left-0'} bottom-full mb-1 flex gap-1 bg-white rounded-full shadow-lg border px-2 py-1 z-50`}
                                    data-testid={`reaction-picker-${msg.id}`}
                                    onMouseLeave={() => { setShowReactionPicker(null); setHoveredMsg(null); }}>
                                    {quickReactions.map(emoji => (
                                      <button key={emoji} onClick={() => handleReaction(msg.id, emoji)}
                                        data-testid={`picker-emoji-${emoji}`}
                                        className="text-lg hover:scale-125 transition-transform px-0.5">
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Scroll to Bottom Button */}
                {showScrollButton && (
                  <div className="absolute bottom-24 right-8 z-10">
                    <Button
                      onClick={scrollToBottom}
                      size="sm"
                      className="rounded-full w-10 h-10 p-0 bg-blue-500 hover:bg-blue-600 shadow-lg"
                      title="Scroll to latest messages"
                      data-testid="scroll-to-bottom-btn"
                    >
                      <ArrowDown className="w-5 h-5" />
                    </Button>
                  </div>
                )}

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t bg-white relative">
                  {/* Reply Preview */}
                  {replyTo && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-slate-50 rounded-lg border-l-4 border-l-blue-500" data-testid="reply-preview">
                      <Reply className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-blue-600" data-testid="reply-preview-sender">
                          {replyTo.sender_name === user.agent_name ? 'You' : replyTo.sender_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate" data-testid="reply-preview-content">
                          {replyTo.type === 'gif' ? 'GIF' : replyTo.content}
                        </p>
                      </div>
                      <button type="button" onClick={() => setReplyTo(null)} data-testid="reply-preview-cancel" className="text-slate-400 hover:text-slate-600 p-0.5">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker && (
                    <div className="absolute bottom-full left-0 mb-2 z-50">
                      <EmojiPicker 
                        onEmojiClick={onEmojiClick}
                        width={320}
                        height={400}
                        previewConfig={{ showPreview: false }}
                      />
                    </div>
                  )}
                  
                  {/* GIF Picker */}
                  {showGifPicker && (
                    <div className="absolute bottom-full left-0 mb-2 z-50 bg-white rounded-xl shadow-xl border border-slate-200 w-[340px] overflow-hidden">
                      <div className="p-3 border-b border-slate-100">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            placeholder="Search GIFs..."
                            className="pl-9 bg-slate-50 border-slate-200"
                            value={gifSearchTerm}
                            onChange={(e) => handleGifSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <p className="text-xs text-slate-400 mt-2 text-center">Powered by GIPHY</p>
                      </div>
                      <div className="h-[300px] overflow-y-auto p-2">
                        {loadingGifs ? (
                          <div className="flex items-center justify-center h-full">
                            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : gifs.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <Play className="w-12 h-12 mb-2 opacity-50" />
                            <p>No GIFs found</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {gifs.map((gif) => (
                              <div
                                key={gif.id}
                                className="cursor-pointer rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                                onClick={() => sendGif(gif)}
                              >
                                <img
                                  src={gif.preview || gif.url}
                                  alt={gif.title || 'GIF'}
                                  className="w-full h-24 object-cover"
                                  loading="lazy"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileUpload}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      title="Attach file"
                    >
                      <Paperclip className="w-5 h-5 text-slate-500" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      title="Add emoji"
                      className={showEmojiPicker ? 'bg-slate-100' : ''}
                    >
                      <Smile className="w-5 h-5 text-slate-500" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={toggleGifPicker}
                      title="Send GIF"
                      className={showGifPicker ? 'bg-slate-100' : ''}
                    >
                      <span className="text-xs font-bold text-slate-500">GIF</span>
                    </Button>
                    <textarea
                      ref={messageInputRef}
                      data-testid="message-input"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onPaste={(e) => {
                        // Handle paste to preserve line breaks from different sources (Windows, Mac, etc.)
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text');
                        // Normalize line breaks: convert \r\n and \r to \n
                        const normalizedText = pastedText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
                        
                        // Insert at cursor position
                        const textarea = e.target;
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const currentValue = newMessage;
                        const newValue = currentValue.substring(0, start) + normalizedText + currentValue.substring(end);
                        setNewMessage(newValue);
                        
                        // Set cursor position after pasted text
                        setTimeout(() => {
                          textarea.selectionStart = textarea.selectionEnd = start + normalizedText.length;
                          // Trigger height adjustment
                          textarea.style.height = 'auto';
                          textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
                        }, 0);
                      }}
                      onKeyDown={(e) => {
                        // Send on Enter (without Shift), new line on Shift+Enter
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (newMessage.trim() && !sendingMessage) {
                            handleSendMessage(e);
                          }
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[40px] max-h-[120px]"
                      disabled={sendingMessage}
                      onFocus={() => setShowEmojiPicker(false)}
                      rows={1}
                      style={{ height: 'auto', overflow: 'hidden' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                      }}
                    />
                    <Button
                      type="submit"
                      data-testid="send-message-btn"
                      disabled={!newMessage.trim() || sendingMessage}
                      className="bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-slate-50 to-white">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="w-12 h-12 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">Welcome to Chat</h3>
                  <p className="text-slate-500">Select a conversation or start a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New Chat Modal */}
        {showNewChat && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>New Chat</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowNewChat(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mb-4"
                />
                <ScrollArea className="h-64">
                  {filteredUsers.map((u) => (
                    <div
                      key={u.email}
                      className="p-3 hover:bg-slate-50 rounded-lg cursor-pointer flex items-center gap-3"
                      onClick={() => startNewChat(u)}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                        {u.agent_name?.charAt(0) || u.email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{u.agent_name}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}

        {/* New Group Modal */}
        {showNewGroup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Create Group</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowNewGroup(false); setSelectedMembers([]); }}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <Input
                  placeholder="Group name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="mb-4"
                />
                <p className="text-sm text-slate-500 mb-2">
                  Selected: {selectedMembers.length} members
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedMembers.map((m) => (
                    <span
                      key={m.email}
                      className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm flex items-center gap-1"
                    >
                      {m.agent_name}
                      <X
                        className="w-4 h-4 cursor-pointer"
                        onClick={() => setSelectedMembers(selectedMembers.filter(s => s.email !== m.email))}
                      />
                    </span>
                  ))}
                </div>
                <ScrollArea className="h-48 mb-4">
                  {allUsers.map((u) => {
                    const isSelected = selectedMembers.some(m => m.email === u.email);
                    return (
                      <div
                        key={u.email}
                        className={`p-3 rounded-lg cursor-pointer flex items-center gap-3 ${
                          isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                        }`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedMembers(selectedMembers.filter(m => m.email !== u.email));
                          } else {
                            setSelectedMembers([...selectedMembers, u]);
                          }
                        }}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          isSelected ? 'bg-blue-500' : 'bg-gradient-to-br from-blue-500 to-emerald-500'
                        }`}>
                          {isSelected ? <Check className="w-5 h-5" /> : u.agent_name?.charAt(0) || u.email.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{u.agent_name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    );
                  })}
                </ScrollArea>
                <Button
                  onClick={createGroup}
                  className="w-full bg-gradient-to-r from-blue-500 to-emerald-500"
                  disabled={!groupName.trim() || selectedMembers.length === 0}
                >
                  Create Group
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Member to Group Modal */}
        {showAddMember && selectedChat?.type === 'group' && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-lg">
                <CardTitle className="text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Add Member to {selectedChat.name}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddMember(false); setAddMemberSearch(''); }} className="text-white hover:bg-white/20">
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent className="pt-4">
                <Input
                  placeholder="Search users..."
                  value={addMemberSearch}
                  onChange={(e) => setAddMemberSearch(e.target.value)}
                  className="mb-4"
                />
                <p className="text-sm text-slate-500 mb-3">
                  Current members: {selectedChat.participants?.length || 0}
                </p>
                <ScrollArea className="h-64">
                  {allUsers
                    .filter(u => 
                      !selectedChat.participants?.includes(u.email) &&
                      (u.agent_name?.toLowerCase().includes(addMemberSearch.toLowerCase()) ||
                       u.email.toLowerCase().includes(addMemberSearch.toLowerCase()))
                    )
                    .map((u) => (
                      <div
                        key={u.email}
                        className="p-3 hover:bg-purple-50 rounded-lg cursor-pointer flex items-center gap-3 border-b"
                        onClick={() => addMemberToGroup(u.email)}
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                          {u.agent_name?.charAt(0) || u.email.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{u.agent_name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                        <UserPlus className="w-5 h-5 text-purple-500" />
                      </div>
                    ))
                  }
                  {allUsers.filter(u => !selectedChat.participants?.includes(u.email)).length === 0 && (
                    <div className="p-8 text-center text-slate-400">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>All users are already in this group</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}
        <Footer />
      </main>
    </div>
  );
};

export default Chat;
